/**
 * Statement processing for bootstrap.
 *
 * Groups statements by context, extracts tags, and pushes the result into the
 * zustand + filter stores. Sentiment is NOT computed here — it is loaded lazily
 * (google model) only when the user filters by sentiment. See
 * useZustand.loadGoogleSentiments.
 */

import { useZustand } from '../../hooks/useZustand'
import { useGraphStore } from '../useGraphStore'
import { useFilterStore } from '../useFilterStore'
import {
    useContextsStore,
    findContextById,
} from '../useContextsStore'
import { formatTopicsFromClusters } from '../../utils/graphTopics'

/**
 * Group statements by context, set zustand state, and seed the filter store's
 * filteredStatements.
 */
export const processStatements = async ({
    statements,
    contextName,
    addContexts,
    isSynthesis = false,
}) => {
    const groupedStatements = {}
    const allContextNames = [contextName, ...addContexts]
    allContextNames.forEach(ctx => {
        groupedStatements[ctx] = []
    })

    const contextsState = useContextsStore.getState()
    statements.forEach(statement => {
        const record = findContextById(contextsState, statement.context)
        const ctxName = record ? record.name : contextName

        if (!groupedStatements[ctxName]) {
            groupedStatements[ctxName] = []
        }
        groupedStatements[ctxName].push(statement)
    })

    const extractedTags = new Set(
        statements.flatMap(statement => statement.tags || [])
    )

    useZustand.setState({
        statements: groupedStatements,
        currentContext: contextName,
        loadedStatements: true,
        tags: extractedTags,
    })

    // For synthesis views, show all statements across contexts.
    // For normal context views, show only the current context's statements.
    const filteredStatements = isSynthesis
        ? statements
        : groupedStatements[contextName] || []

    useFilterStore.getState().setFilteredStatements(filteredStatements)
}

/**
 * Set initial topics from graph communities (fallback names).
 */
export const initTopicsFromGraph = () => {
    const communities = useGraphStore.getState().getCommunities()
    if (communities && communities.length > 0) {
        const formattedCommunities = formatTopicsFromClusters(communities)
        useZustand.getState().setTopics(formattedCommunities, true)
        useFilterStore.getState().updateFilteredStatementsWithCommunities()
    }
}
